import { WalletPlatform } from '../wallets/types';

export interface ITransactionCustomer {
  id: string;
  name: string;
  kycStatus: string;
  country: string;
}

interface IDiscountCodeSummary {
  id: string;
  name: string;
  code: string;
}

export enum TransactionType {
  Deposit = 'DEPOSIT',
  Withdrawal = 'WITHDRAWAL',
  Correction = 'CORRECTION',
  Bonus = 'BONUS',
  DepositBonus = 'DEPOSIT_BONUS',
  Fee = 'FEE',
  CoinsExchange = 'COINS_EXCHANGE',
  WalletTransfer = 'WALLET_TRANSFER',
  CommissionPayout = 'COMMISSION_PAYOUT',
  PropPurchase = 'PROP_PURCHASE',
}

export enum TransactionStatus {
  // Created request
  Created = 'created',
  // Pending response from PSP (deposits) or approval from backoffice admin (withdrawals)
  Pending = 'pending',
  // Successful
  Successful = 'successful',
  // Canceled by user interaction
  Canceled = 'canceled',
  // Failed because e.g. payment provider has declined it
  Failed = 'failed',
  // Technical error
  Error = 'error',
}

export enum WithdrawalStatus {
  Approved = 'approved',
  Rejected = 'rejected',
  Refunded = 'refunded',
}

export interface ITransaction {
  id: string;
  type: TransactionType;
  customer: ITransactionCustomer;
  netAmountInUSD: number;
  createdAt: string;
  status: TransactionStatus;
  method: string | null;
  provider: string | null;
  providerRef: string | null;
  amount: number;
  comment: string | null;
  currency: string;
  wallet?: number;
  /**
   * The platform of the wallet the money moved on. Absent when the transaction has no wallet — a
   * commission payout, say — so the column renders blank rather than claiming Wintrado.
   */
  platform?: WalletPlatform | null;
  creditedAt?: string | null;
  walletAddress: string | null;
  adminId: string | null;
  /**
   * The MT5 deal this transaction's money moved as. Null for Wintrado accounts, which have no
   * equivalent handle, and also for an MT5 transaction whose money has not reached the account yet.
   */
  mt5DealId?: string | null;
  ipAddress?: string | null;
  conversionFrom?: string | null;
  conversionFromAmount?: string | null;
  conversionTo?: string | null;
  conversionToAmount?: string | null;
  conversionRate?: string | null;
  conversionSource?: string | null;
  conversionDate?: string | null;
  discountCode?: IDiscountCodeSummary | null;
  affiliateCommission?: {
    introducerId: string;
    referralId: string;
    referralName?: string;
    commissionType: string;
    actionType: string;
    actionDetails: string;
  };
  propFunnelId?: string | null;
}

export interface IWithdrawal {
  id: string;
  amount: number;
  currency: string;
  createdAt: string;
  customer: ITransactionCustomer;
  creditedAt: string;
  comment: string;
  status: string;
  walletAddress: string | null;
  provider: string;
  adminId: string | null;
}

// Qualification types for affiliate FTD feature
export interface IQualificationCustomer {
  id: string;
  firstname: string | null;
  lastname: string | null;
  email: string;
}

export interface IQualificationWallet {
  id: string;
  customer: IQualificationCustomer | null;
}

export interface IQualificationTransaction {
  id: string;
  amount: number;
  currency: string;
  type: string;
  status: TransactionStatus;
  createdAt: string;
  isQualifiedForAffiliate: boolean;
  qualifiedById: string | null;
  qualifiedAt: string | null;
  wallet: IQualificationWallet | null;
  qualifiedBy: IQualificationCustomer | null;
}

export interface IQualificationResult {
  transaction: IQualificationTransaction;
  cpaAwarded: boolean;
  affiliateFTDSet: boolean;
}

export interface IQualificationListResponse {
  transactions: IQualificationTransaction[];
  total: number;
}
